import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { locationOrVirtual } from "@/lib/virtual";

export interface PersonData {
  first_name: string;
  last_name: string;
  email?: string;
  gender?: "female" | "male" | "other" | null;
}

/** Upsert a team by name. Returns the team row. */
export async function upsertTeam(name: string) {
  const { data: team, error } = await supabase
    .from('team')
    .upsert({ name }, { onConflict: 'name' })
    .select()
    .single();

  if (error) throw error;
  return team;
}

/** Look up a person by exact email (case-insensitive). Used for a pre-flight
 * duplicate-email check when manually adding a new participant, so we can
 * offer to link to the existing person instead of blind-inserting a new
 * person_email row and hitting the unique-email constraint. */
export async function findPersonByEmail(
  email: string,
): Promise<{ id: number; first_name: string; last_name: string | null; gender: string | null; emails: string[] } | null> {
  const e = email.trim();
  if (!e) return null;

  // Exact match only: emails are normalized to lowercase by the schema, so
  // an .eq() on the lowercased input is both correct and exact. (.ilike()
  // treats % and _ as wildcards, so e.g. foo_bar@example.com could match an
  // unrelated row and silently link to the wrong person.)
  const { data, error } = await supabase
    .from('person_email')
    .select('person_id, person:person_id(id, first_name, last_name, gender, person_email(email))')
    .eq('email', e.toLowerCase())
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  if (!data?.person) return null;

  const person = data.person as unknown as {
    id: number;
    first_name: string;
    last_name: string | null;
    gender: string | null;
    person_email: { email: string }[] | null;
  };

  return {
    id: person.id,
    first_name: person.first_name,
    last_name: person.last_name,
    gender: person.gender,
    emails: (person.person_email ?? []).map((pe) => pe.email),
  };
}

/** Delete persons by IDs. Cascade removes person_emails. */
export async function deletePersons(ids: number[]) {
  if (!ids.length) return;
  const { error } = await supabase
    .from('person')
    .delete()
    .in('id', ids);

  if (error) throw error;
}

/** Delete teams by IDs. */
export async function deleteTeams(ids: number[]) {
  if (!ids.length) return;
  const { error } = await supabase
    .from('team')
    .delete()
    .in('id', ids);

  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Batch helpers – reduce O(n) HTTP round-trips to O(1)
// ---------------------------------------------------------------------------

/** Batch insert new persons. Returns array of created person rows. */
export async function insertPersonsBatch(
  data: PersonData[]
): Promise<Array<{ id: number; first_name: string; last_name: string }>> {
  if (!data.length) return [];
  const { data: persons, error } = await supabase
    .from('person')
    .insert(
      data.map(d => ({
        first_name: d.first_name,
        last_name: d.last_name,
        ...(d.gender ? { gender: d.gender } : {}),
      })),
    )
    .select();

  if (error) throw error;
  return persons ?? [];
}

/** Batch update existing persons by ID. */
export async function updatePersonsBatch(
  data: Array<{ id: number; first_name: string; last_name: string; gender?: "female" | "male" | "other" | null }>
): Promise<void> {
  if (!data.length) return;
  // Supabase doesn't support batch update by different IDs in one call,
  // so we run them in parallel
  const results = await Promise.all(
    data.map(({ id, ...fields }) =>
      supabase.from('person').update(fields).eq('id', id)
    ),
  );
  const firstError = results.find(r => r.error);
  if (firstError?.error) throw firstError.error;
}

/** Batch upsert emails for persons. Skips entries without email. */
export async function upsertPersonEmailsBatch(
  entries: Array<{ person_id: number; email: string }>
): Promise<void> {
  const valid = entries
    .map(e => ({
      person_id: e.person_id,
      email: e.email.trim().toLowerCase(),
      is_primary: true,
    }))
    .filter(e => e.email);
  if (!valid.length) return;
  const { error } = await supabase
    .from('person_email')
    .upsert(valid, { onConflict: 'email' });

  if (error) throw error;
}

export async function getExistingPersons() {
  const { data, error } = await supabase
    .from('person')
    .select('*, person_email(email)')
    .order('first_name');

  if (error) throw error;
  return data;
}

/** Search persons by name or email. Returns up to `limit` matches. */
export async function searchPersons(
  query: string,
  limit = 10,
): Promise<Array<{ id: number; first_name: string; last_name: string | null; gender: string | null; emails: string[] }>> {
  const q = query.trim();
  if (!q) return [];

  // Search persons whose name or email matches the query
  // Use ilike on person fields + a separate email search, then merge
  const nameParts = q.split(/\s+/);
  const nameFilter =
    nameParts.length >= 2
      ? `and(first_name.ilike.%${nameParts[0]}%,last_name.ilike.%${nameParts.slice(1).join(" ")}%)`
      : `or(first_name.ilike.%${q}%,last_name.ilike.%${q}%)`;

  const [nameResult, emailResult] = await Promise.all([
    supabase
      .from('person')
      .select('id, first_name, last_name, gender, person_email(email)')
      .or(nameFilter)
      .limit(limit),
    supabase
      .from('person_email')
      .select('person_id, email, person:person_id(id, first_name, last_name, gender)')
      .ilike('email', `%${q}%`)
      .limit(limit),
  ]);

  if (nameResult.error) throw nameResult.error;
  if (emailResult.error) throw emailResult.error;

  // Merge results by person ID
  const personMap = new Map<number, { id: number; first_name: string; last_name: string | null; gender: string | null; emails: string[] }>();

  for (const p of nameResult.data ?? []) {
    personMap.set(p.id, {
      id: p.id,
      first_name: p.first_name,
      last_name: p.last_name,
      gender: p.gender,
      emails: (p.person_email ?? []).map((pe: { email: string }) => pe.email),
    });
  }

  for (const row of emailResult.data ?? []) {
    const person = row.person;
    if (!person) continue;
    if (!personMap.has(person.id)) {
      personMap.set(person.id, {
        id: person.id,
        first_name: person.first_name,
        last_name: person.last_name,
        gender: person.gender,
        emails: [row.email],
      });
    } else {
      const existing = personMap.get(person.id)!;
      if (!existing.emails.includes(row.email)) {
        existing.emails.push(row.email);
      }
    }
  }

  return Array.from(personMap.values()).slice(0, limit);
}

export async function getExistingTeams() {
  const { data, error } = await supabase
    .from('team')
    .select('*')
    .order('name');

  if (error) throw error;
  return data;
}

/** Search teams by name. Returns up to `limit` matches. */
export async function searchTeams(
  query: string,
  limit = 10,
): Promise<Array<{ id: number; name: string }>> {
  const q = query.trim();
  if (!q) return [];

  const { data, error } = await supabase
    .from('team')
    .select('id, name')
    .ilike('name', `%${q}%`)
    .order('name')
    .limit(limit);

  if (error) throw error;
  return data ?? [];
}

/** Fetch persons that are members of a specific team (across all participations). */
export async function getTeamMembers(
  teamId: number,
): Promise<Array<{ id: number; first_name: string; last_name: string | null; gender: string | null; emails: string[] }>> {
  const { data: members, error: membersError } = await supabase
    .from('participation_member')
    .select('person_id')
    .eq('team_id', teamId);

  if (membersError) throw membersError;
  const personIds = [...new Set((members ?? []).map((m) => m.person_id))];
  if (!personIds.length) return [];

  const { data: persons, error: personsError } = await supabase
    .from('person')
    .select('id, first_name, last_name, gender, person_email(email)')
    .in('id', personIds);

  if (personsError) throw personsError;
  return (persons ?? []).map((p) => ({
    id: p.id,
    first_name: p.first_name,
    last_name: p.last_name,
    gender: p.gender,
    emails: (p.person_email ?? []).map((pe: { email: string }) => pe.email),
  }));
}

/** Fetch all distinct person → team mappings from participation_member. */
export async function getPersonTeamMappings(): Promise<Map<number, number[]>> {
  const { data, error } = await supabase
    .from('participation_member')
    .select('person_id, team_id')
    .not('team_id', 'is', null);

  if (error) throw error;

  const map = new Map<number, number[]>();
  for (const row of data ?? []) {
    const pid = row.person_id;
    const tid = row.team_id as number;
    if (!map.has(pid)) map.set(pid, []);
    const arr = map.get(pid)!;
    if (!arr.includes(tid)) arr.push(tid);
  }
  return map;
}

/** Fetch persons and locations associated with each team via participation_member → participation */
export async function getTeamDetails(teamIds: number[]): Promise<
  Map<number, { persons: string[]; locations: string[] }>
> {
  if (!teamIds.length) return new Map();

  // Step 1: get participation_members for these teams
  const { data: members, error: membersError } = await supabase
    .from('participation_member')
    .select('team_id, person_id, participation_id')
    .in('team_id', teamIds);

  if (membersError) throw membersError;
  if (!members?.length) return new Map();

  // Step 2: get person names and participation locations in parallel
  const personIds = [...new Set(members.map((m) => m.person_id))];
  const participationIds = [...new Set(members.map((m) => m.participation_id))];

  const [
    { data: persons, error: personsError },
    { data: participations, error: partError },
  ] = await Promise.all([
    supabase.from('person').select('id, first_name, last_name').in('id', personIds),
    supabase.from('participation').select('id, location_id').in('id', participationIds),
  ]);

  if (personsError) throw personsError;
  if (partError) throw partError;

  const personById = new Map(
    (persons ?? []).map((p) => [p.id, `${p.first_name} ${p.last_name ?? ""}`.trim()]),
  );

  // Step 3: get location details for the location IDs found
  const locationIds = [
    ...new Set(
      (participations ?? []).map((p) => p.location_id).filter((id): id is number => id != null),
    ),
  ];
  const { data: locations, error: locError } = await supabase
    .from('location')
    .select('id, location_name, city')
    .in('id', locationIds);

  if (locError) throw locError;

  const locationById = new Map((locations ?? []).map((l) => [l.id, l]));
  const locationByPartId = new Map(
    (participations ?? []).map((p) => {
      const l = locationOrVirtual(locationById, p.location_id);
      return [p.id, l ? `${l.location_name}, ${l.city}` : null] as const;
    }),
  );

  // Step 4: assemble results
  const result = new Map<number, { persons: Set<string>; locations: Set<string> }>();
  for (const row of members) {
    const tid = row.team_id!;
    if (!result.has(tid)) {
      result.set(tid, { persons: new Set(), locations: new Set() });
    }
    const entry = result.get(tid)!;
    const name = personById.get(row.person_id);
    if (name) {
      entry.persons.add(name);
    }
    const loc = locationByPartId.get(row.participation_id);
    if (loc) {
      entry.locations.add(loc);
    }
  }

  const out = new Map<number, { persons: string[]; locations: string[] }>();
  for (const [tid, { persons: p, locations: l }] of result) {
    out.set(tid, { persons: [...p].sort(), locations: [...l].sort() });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Admin: person summaries (backed by v_person_summary view)
// ---------------------------------------------------------------------------

export interface PersonSummaryEmail {
  id: number;
  email: string;
  is_primary: boolean;
}

export interface PersonSummaryLocation {
  city: string;
  country: string | null;
}

export interface PersonSummary {
  id: number;
  first_name: string;
  last_name: string;
  gender: string | null;
  unsubscribed_at: string | null;
  emails: PersonSummaryEmail[];
  event_count: number;
  /** Cities/countries of events the person played at (not their home). */
  locations: PersonSummaryLocation[];
  home_city: string | null;
  home_country: string | null;
}

/** Fetch all persons with emails, participation count and locations (admin). */
export async function fetchPersonSummaries(): Promise<PersonSummary[]> {
  const { data, error } = await supabase
    .from("v_person_summary" as "person")
    .select("*")
    .order("first_name")
    .order("last_name")
    .order("id");

  if (error) throw error;

  return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
    id: row.id as number,
    first_name: row.first_name as string,
    last_name: (row.last_name as string) ?? "",
    gender: (row.gender as string) ?? null,
    unsubscribed_at: (row.unsubscribed_at as string) ?? null,
    emails: (row.emails ?? []) as PersonSummaryEmail[],
    event_count: (row.event_count as number) ?? 0,
    locations: (row.locations ?? []) as PersonSummaryLocation[],
    home_city: (row.home_city as string) ?? null,
    home_country: (row.home_country as string) ?? null,
  }));
}

export function createPerson(row: TablesInsert<"person">) {
  return supabase
    .from("person")
    .insert(row)
    .select("id")
    .single();
}

export function updatePerson(id: number, patch: TablesUpdate<"person">) {
  return supabase.from("person").update(patch).eq("id", id);
}

export function deletePerson(id: number) {
  return supabase.from("person").delete().eq("id", id);
}

export function insertPersonEmail(row: TablesInsert<"person_email">) {
  return supabase.from("person_email").insert(row);
}

export function setPersonEmailPrimary(id: number, isPrimary: boolean) {
  return supabase.from("person_email").update({ is_primary: isPrimary }).eq("id", id);
}

export function deletePersonEmail(id: number) {
  return supabase.from("person_email").delete().eq("id", id);
}

/** Who currently owns an email (for actionable duplicate-email errors). */
export function findPersonEmailOwner(email: string) {
  return supabase
    .from("person_email")
    .select("person_id, person:person_id(first_name, last_name)")
    .eq("email", email)
    .maybeSingle();
}

/** Landing "notify me" signup. Creates a new person + primary email; an email
 * that is already registered is a silent no-op. Blank optional fields are
 * stored as NULL server-side. */
export function subscribeForUpdates(input: {
  firstName: string;
  lastName: string;
  email: string;
  homeCity: string;
  homeCountry: string;
}) {
  return supabase.rpc("subscribe_for_updates", {
    p_first_name: input.firstName,
    p_last_name: input.lastName,
    p_email: input.email,
    p_home_city: input.homeCity,
    p_home_country: input.homeCountry,
  });
}

/** RPC stamps server now() (not the browser clock) and enforces admin. */
export function setPersonUnsubscribed(personId: number, unsubscribe: boolean) {
  return supabase.rpc("set_person_unsubscribed", { p_person_id: personId, p_unsubscribe: unsubscribe });
}

export function mergePersons(primaryId: number, secondaryIds: number[]) {
  return supabase.rpc("merge_persons", { p_primary_id: primaryId, p_secondary_ids: secondaryIds });
}
